import { Button } from '@strata/react-buttons';
import { MessagingBlock } from '@strata/react-messaging-block';
import { PageTitle } from '@strata/react-page-title';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { OrtfRemainingTransferScriptsTab } from './OrtfRemainingTransferScriptsTab';
import { ScriptCountBreakdown } from './ScriptCountBreakdown';
import {
    scriptCountBreakdownColumns,
    scriptCountBreakdownSortDefaults,
    treatmentsNotFoundColumns,
    treatmentsNotFoundSortDefaults,
} from './scriptCountBreakdownConstants';

const TOTALS_LABELS = new Set(['Remaining Transfers', 'Treatments Not Found']);

export const OrtfRemainingTransfersTabV2 = ({
    isLoading = false,
    remainingTransfersBreakdown = undefined,
    remainingTransfersId = undefined,
}) => {
    const [showingAllScripts, setShowingAllScripts] = useState(false);

    if (showingAllScripts && Boolean(remainingTransfersId)) {
        return (
            <OrtfRemainingTransferScriptsTab
                onBack={() => setShowingAllScripts(false)}
                remainingTransfersId={remainingTransfersId}
            />
        );
    }

    if (!remainingTransfersBreakdown && !isLoading) {
        return (
            <MessagingBlock
                size={MessagingBlock.Sizes.MEDIUM}
                secondaryText="Generate outbound files to view remaining transfers."
            />
        );
    }

    const { totals, pharmacies, treatmentsNotFound } = remainingTransfersBreakdown ?? {};
    const displayTotals = totals?.filter(({ label }) => TOTALS_LABELS.has(label)) ?? [];

    return (
        <div className="padding__around--medium">
            <div
                className="margin__bottom--medium flex__gap--medium"
                style={{ alignItems: 'center', justifyContent: 'space-between' }}
            >
                <PageTitle title="Remaining Transfers" />
                <Button
                    disabled={!remainingTransfersId}
                    intent={Button.Intent.PRIMARY}
                    label="View all scripts"
                    onClick={() => setShowingAllScripts(true)}
                />
            </div>
            <ScriptCountBreakdown
                isLoading={isLoading}
                totals={displayTotals}
                sections={(pharmacies ?? []).map(({ pharmacyName, rows = [] }) => {
                    const total = rows.reduce(
                        (sum, { prescriptions }) => sum + (prescriptions ?? 0),
                        0,
                    );
                    return {
                        title: `${pharmacyName} (${total})`,
                        rows,
                    };
                })}
                sectionsEmptyMessage="No remaining transfers available."
                sectionEmptyMessage="No remaining transfers for this pharmacy."
                sideSection={{
                    title: 'Treatments Not Found',
                    rows: treatmentsNotFound?.sources ?? [],
                    emptyMessage: 'No treatments not found.',
                }}
                columns={scriptCountBreakdownColumns}
                sideSectionColumns={treatmentsNotFoundColumns}
                sideSectionSortProperties={treatmentsNotFoundSortDefaults}
                sortProperties={scriptCountBreakdownSortDefaults}
            />
        </div>
    );
};

OrtfRemainingTransfersTabV2.propTypes = {
    isLoading: PropTypes.bool,
    remainingTransfersBreakdown: PropTypes.shape({
        totals: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.number,
            }),
        ),
        pharmacies: PropTypes.arrayOf(
            PropTypes.shape({
                pharmacyName: PropTypes.string,
                rows: PropTypes.arrayOf(PropTypes.shape({})),
            }),
        ),
        treatmentsNotFound: PropTypes.shape({
            sources: PropTypes.arrayOf(
                PropTypes.shape({
                    source: PropTypes.string,
                    prescriptions: PropTypes.number,
                }),
            ),
        }),
    }),
    remainingTransfersId: PropTypes.string,
};
